// Staff & HR section kit — the few helpers the staff record, the staff list, Teams, Leave,
// Reimbursements and Payslips share. Everything visual builds on the app kit (components/ui); this
// file only adds what is specific to HR records:
//
//   • ONE expiry rule (expiryStatus / ExpiryBadge). The staff record used to work it out five
//     different ways (<= 30 days on Overview, < 30 on Documents, both on Training, a millisecond
//     constant on Expired documents), so the same CPR certificate read "30d left" on one tab and
//     "Valid" on the next.
//   • ONE label map for roles, employment types, leave types and genders — no more 'carer',
//     'part time' or 'public_holiday' leaking onto the screen.
//   • NDIS worker screening worked out from the clearance record AND the documents on file, so the
//     header can never say "Not checked" while a current screening document sits on the Documents tab.
//   • useStaffRecord: the staff record every tab needs, cached so the header draws instantly when you
//     switch tabs instead of flashing empty while the page refetches.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import api from '../../utils/api';
import { Dialog, Field, fmtDate, fmtDayMonth, humanise, plural } from '../../components/ui';

/* ── Expiry ─────────────────────────────────────────────────────────────────── */

// "Expiring" means within the next 30 days, INCLUSIVE — a document that expires in exactly 30 days
// is expiring, on every tab. The server's documents summary uses the same rule.
export const SOON_DAYS = 30;
const DAY_MS = 86400000;

// A 'YYYY-MM-DD' value is a calendar day, not an instant: read it in local time so it can never slip
// a day either side of midnight.
export function toDay(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : new Date(v.getFullYear(), v.getMonth(), v.getDate());
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// → { state: 'none' | 'expired' | 'soon' | 'valid', daysLeft, date }
export function expiryStatus(value, today = new Date()) {
  const d = toDay(value);
  if (!d) return { state: 'none', daysLeft: null, date: null };
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysLeft = Math.round((d - t) / DAY_MS);
  return { state: daysLeft < 0 ? 'expired' : daysLeft <= SOON_DAYS ? 'soon' : 'valid', daysLeft, date: d };
}

// Sort key: most overdue first, then the soonest expiry, then things that never expire.
export const expirySortKey = (value) => {
  const { daysLeft } = expiryStatus(value);
  return daysLeft == null ? Number.POSITIVE_INFINITY : daysLeft;
};

const sameYear = (d) => d && d.getFullYear() === new Date().getFullYear();

export function expiryText(value) {
  const { state, daysLeft, date } = expiryStatus(value);
  if (state === 'none') return '';
  if (state === 'expired') return `Expired ${sameYear(date) ? fmtDayMonth(date) : fmtDate(date)}`;
  if (state === 'soon') {
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    return `Expires in ${daysLeft} days`;
  }
  return `Valid to ${fmtDate(date)}`;
}

// 'Expired 3 Sep' (red) · 'Expires in 30 days' (amber) · 'Valid to 3 Mar 2029' (quiet, with a tick).
// Colour only marks a problem; a valid date stays calm so the problems stand out.
export function ExpiryBadge({ date, none = 'No expiry', className }) {
  const { state, date: d } = expiryStatus(date);
  if (state === 'none') return <span className={clsx('text-[12.5px] text-slate-500', className)}>{none}</span>;
  const text = expiryText(date);
  if (state === 'expired') return <span className={clsx('badge-red whitespace-nowrap', className)} title={`Expired ${fmtDate(d)}`}>{text}</span>;
  if (state === 'soon') return <span className={clsx('badge-yellow whitespace-nowrap', className)} title={`Expires ${fmtDate(d)}`}>{text}</span>;
  return (
    <span className={clsx('inline-flex items-center gap-1 text-[12.5px] text-slate-600 whitespace-nowrap', className)}>
      <Check size={13} className="shrink-0 text-emerald-600" aria-hidden="true" />{text}
    </span>
  );
}

/* ── Labels ─────────────────────────────────────────────────────────────────── */

// Identity attributes (role, employment, team, category) are words, never colours.
export const ROLE_LABEL = {
  admin: 'Admin', office_user: 'Office user', carer: 'Carer', support_worker: 'Support worker',
  staff: 'Staff', nurse: 'Nurse', coordinator: 'Coordinator', super_admin: 'Super admin',
};
export const roleLabel = (r) => (r ? ROLE_LABEL[r] || humanise(r) : '—');

export const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'casual', label: 'Casual' },
  { value: 'contractor', label: 'Contractor' },
];
export const employmentLabel = (e) => {
  if (!e) return '—';
  const key = String(e).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return (EMPLOYMENT_TYPES.find((t) => t.value === key) || {}).label || humanise(e);
};

// One leave vocabulary for the Leave page and the staff record's Leave tab (they used to store
// 'annual' on one and 'Annual Leave' on the other, which split a worker's balances in two).
export const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual' },
  { value: 'sick', label: 'Sick' },
  { value: 'personal', label: "Personal/carer's" },
  { value: 'carer', label: "Carer's" },
  { value: 'parental', label: 'Parental' },
  { value: 'long_service', label: 'Long service' },
  { value: 'public_holiday', label: 'Public holiday' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'other', label: 'Other' },
];
export const leaveTypeLabel = (t) => {
  if (!t) return 'Leave';
  const hit = LEAVE_TYPES.find((x) => x.value === t);
  if (hit) return hit.label;
  // Older requests were stored as free text ('Annual Leave'): show them as they were written.
  return humanise(t);
};

export const GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];
// Match a stored value ('Male', 'male', 'non_binary') to an option; anything else was self-described.
export const genderOption = (g) => {
  const v = String(g || '').trim().toLowerCase();
  if (!v) return null;
  return GENDER_OPTIONS.find((o) => o.value === v || o.label.toLowerCase() === v || o.value === v.replace(/[\s-]+/g, '_')) || null;
};
export const genderLabel = (g) => (genderOption(g) || {}).label || (g ? String(g) : '—');

export const staffName = (s) => (s ? `${s.first_name || ''} ${s.last_name || ''}`.trim() : '');

/* ── Documents ──────────────────────────────────────────────────────────────── */

// Staff documents are stored with `file_path` (/uploads/staff-documents/...). The browser's session
// cookie authorises /uploads, so no token goes in the address.
export const docHref = (d) => (d && (d.file_path || d.file_url)) || null;
export const docName = (d) => {
  const n = (d && (d.name || d.filename)) || '';
  try { return decodeURIComponent(n); } catch { return n; }
};

const NDIS_RE = /ndis/i;
const SCREENING_RE = /screen|clearance|check|worker/i;
export const isScreeningDoc = (d) => {
  const t = `${(d && d.category) || ''} ${(d && d.document_type) || ''} ${(d && d.name) || ''}`;
  return NDIS_RE.test(t) && SCREENING_RE.test(t);
};

// NDIS worker screening, from the clearance record first and a screening document second.
// → { state, label, short, tone: 'success'|'warning'|'danger'|'neutral', expiry, action }
export function ndisScreening(staff, docs = []) {
  const s = staff || {};
  const status = String(s.ndis_clearance_status || 'not_checked').toLowerCase();
  if (status === 'clear') {
    const ex = expiryStatus(s.ndis_clearance_expiry);
    if (ex.state === 'expired') return { state: 'expired', short: 'Screening expired', label: `NDIS screening ${expiryText(s.ndis_clearance_expiry).toLowerCase()}`, tone: 'danger', expiry: s.ndis_clearance_expiry };
    if (ex.state === 'soon') return { state: 'expiring', short: 'Screening expiring', label: `NDIS screening ${expiryText(s.ndis_clearance_expiry).toLowerCase()}`, tone: 'warning', expiry: s.ndis_clearance_expiry };
    return { state: 'verified', short: 'Screening verified', label: ex.state === 'valid' ? `NDIS screening verified, ${expiryText(s.ndis_clearance_expiry).toLowerCase()}` : 'NDIS screening verified', tone: 'success', expiry: s.ndis_clearance_expiry };
  }
  if (status === 'expired') return { state: 'expired', short: 'Screening expired', label: 'NDIS screening expired', tone: 'danger', expiry: s.ndis_clearance_expiry };
  if (status === 'invalid') return { state: 'invalid', short: 'Screening number invalid', label: 'NDIS screening number is not valid', tone: 'danger' };
  if (status === 'in_progress') return { state: 'in_progress', short: 'Screening in progress', label: 'NDIS screening in progress', tone: 'warning' };
  const doc = (docs || []).filter(Boolean).filter(isScreeningDoc)
    .sort((a, b) => expirySortKey(b.expiry_date) - expirySortKey(a.expiry_date))[0];
  if (doc) {
    const ex = expiryStatus(doc.expiry_date);
    if (ex.state === 'expired') return { state: 'document_expired', short: 'Screening document expired', label: `NDIS screening document ${expiryText(doc.expiry_date).toLowerCase()}`, tone: 'danger', expiry: doc.expiry_date, doc };
    return { state: 'document', short: 'Document on file, not verified', label: 'NDIS screening document on file, not verified', tone: 'warning', expiry: doc.expiry_date, doc };
  }
  return { state: 'missing', short: 'No screening recorded', label: 'No NDIS worker screening recorded', tone: 'neutral' };
}

const TONE_PILL = { success: 'badge-green', warning: 'badge-yellow', danger: 'badge-red', neutral: 'badge-gray' };
export const tonePill = (tone) => TONE_PILL[tone] || 'badge-gray';

// Compliance state for one worker in the staff list: 'n gaps' (rose), 'On hold' (amber),
// 'Compliant' (quiet, with a tick), or 'No requirements' when none are set for their role — so a
// worker is never shown as compliant against nothing.
export function ComplianceBadge({ gaps, onHold, required }) {
  const g = Number(gaps) || 0;
  const parts = [];
  if (g > 0) parts.push(<span key="g" className="badge-red whitespace-nowrap" title="Missing or expired required qualifications">{plural(g, 'gap')}</span>);
  if (onHold) parts.push(<span key="h" className="badge-yellow whitespace-nowrap" title="Excluded from rostering">On hold</span>);
  if (parts.length) return <span className="inline-flex flex-wrap items-center gap-1">{parts}</span>;
  if (required != null && Number(required) === 0) {
    return <span className="text-[12.5px] text-slate-500 whitespace-nowrap" title="No compliance requirements are set for this role">No requirements</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-[12.5px] text-slate-600 whitespace-nowrap">
      <Check size={13} className="shrink-0 text-emerald-600" aria-hidden="true" />Compliant
    </span>
  );
}

/* ── Small controls ─────────────────────────────────────────────────────────── */

// Icon-only action: always labelled, 32px hit area, slate until hovered (red only for danger).
export function IconButton({ label, icon: Icon, onClick, danger = false, href, target, rel, disabled, className, size = 15 }) {
  const cls = clsx(
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors duration-150',
    'disabled:cursor-not-allowed disabled:opacity-40',
    danger ? 'hover:bg-rose-50 hover:text-rose-600' : 'hover:bg-slate-100 hover:text-slate-800',
    className,
  );
  if (href) {
    return (
      <a href={href} target={target} rel={rel} aria-label={label} title={label} className={cls}>
        <Icon size={size} aria-hidden="true" />
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} className={cls}>
      <Icon size={size} aria-hidden="true" />
    </button>
  );
}

// The shared Dialog re-runs its focus effect whenever `onClose` changes identity, so an inline
// `onClose={() => …}` would pull focus back to the first field on every keystroke. Every dialog in
// this section passes its close handler through here, which keeps one stable function.
export function useStableCallback(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args) => (ref.current ? ref.current(...args) : undefined), []);
}

// A confirm step that records WHY — voiding a contract, declining leave, rejecting a claim.
// Replaces window.prompt: styled, Escape-able, and the reason can be required.
export function ReasonDialog({
  open, onClose, onConfirm, title, description, label = 'Reason', required = false, placeholder,
  confirmLabel = 'Confirm', danger = false, busy = false, children,
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const formId = useId();
  useEffect(() => { if (open) { setReason(''); setError(''); } }, [open]);
  const close = useStableCallback(() => { if (!busy && onClose) onClose(); });
  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (busy) return;
    const r = reason.trim();
    if (required && !r) { setError('Enter a reason to continue.'); return; }
    onConfirm(r);
  };
  return (
    <Dialog
      open={open} onClose={close} title={title} description={description} size="sm"
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={close} disabled={busy}>Cancel</button>
          <button type="submit" form={formId} className={danger ? 'btn-danger' : 'btn-primary'} disabled={busy}>
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} className="space-y-4">
        {children}
        <Field label={label} required={required} error={error} hint={required ? undefined : 'Optional'}>
          {(id) => (
            <textarea
              id={id} data-autofocus rows={3} className="input-field resize-y" value={reason} placeholder={placeholder}
              aria-invalid={!!error || undefined}
              onChange={(e) => { setReason(e.target.value); if (error) setError(''); }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

// "Unsaved changes" guard for long forms: warns before the tab is closed or reloaded.
export function useBeforeUnload(active) {
  useEffect(() => {
    if (!active) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);
}

/* ── The staff record ───────────────────────────────────────────────────────── */

// Last-seen copy of each staff record (tax and bank fields stripped) so a tab can draw the header
// straight away and refresh underneath.
const recordCache = new Map();
const SENSITIVE = ['tfn', 'bank_bsb', 'bank_account_number'];
const safeCopy = (s) => {
  if (!s || typeof s !== 'object') return s;
  const c = { ...s };
  SENSITIVE.forEach((k) => { delete c[k]; });
  return c;
};

export function forgetStaffRecord(id) { recordCache.delete(id); }

export function useStaffRecord(id) {
  const [state, setState] = useState(() => ({ staff: (id && recordCache.get(id)) || null, loading: true, error: null }));
  const alive = useRef(true);
  const seq = useRef(0);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const reload = useCallback(async () => {
    if (!id) return null;
    const mine = ++seq.current;
    try {
      const { data } = await api.get(`/staff/${id}`);
      recordCache.set(id, safeCopy(data));
      if (alive.current && mine === seq.current) setState({ staff: data, loading: false, error: null });
      return data;
    } catch (e) {
      const status = e && e.response && e.response.status;
      const message = status === 404
        ? 'This staff member could not be found. They may have been permanently deleted.'
        : ((e && e.response && e.response.data && e.response.data.message) || "We couldn't load this staff member. Check your connection, then try again.");
      if (alive.current && mine === seq.current) setState((s) => ({ ...s, loading: false, error: message }));
      return null;
    }
  }, [id]);

  useEffect(() => {
    setState({ staff: (id && recordCache.get(id)) || null, loading: true, error: null });
    reload();
  }, [id, reload]);

  const setStaff = useCallback((next) => {
    setState((s) => ({ ...s, staff: typeof next === 'function' ? next(s.staff) : next }));
  }, []);

  return { staff: state.staff, loading: state.loading, error: state.error, reload, setStaff };
}
